import firebase from "config/firebase";
import { toast } from "react-toastify";

let unsubscribe = null;

export const getReadNotifications =
  (fanId, lastDoc = null, onSuccess = () => {}) =>
  async (dispatch) => {
    try {
      let query = firebase
        .firestore()
        .collection("fan_notifications")
        .where("isRead", "array-contains", fanId)
        .orderBy("createdAt", "desc")
        .limit(10);

      if (lastDoc) {
        query = query.startAfter(lastDoc);
      }

      if (unsubscribe) {
        unsubscribe();
      }

      unsubscribe = query.onSnapshot(async (snapshot) => {
        const notifications = [];

        for (const doc of snapshot.docs) {
          const notificationData = { id: doc.id, ...doc.data() };
          const athleteId = notificationData.athleteId;

          // Only include notifications that are for this specific fan
          // Either no fanId (broadcast) OR matches this fanId
          const isForThisFan = !notificationData.fanId || notificationData.fanId === fanId;
          
          if (!isForThisFan) {
            continue; // Skip this notification
          }

          try {
            const userSnapshot = await firebase
              .firestore()
              .collection("users")
              .doc(athleteId)
              .get();

            if (userSnapshot.exists) {
              notificationData.athleteData = {
                id: userSnapshot.id,
                ...userSnapshot.data(),
              };
            } else {
              notificationData.athleteData = null;
            }
          } catch (userErr) {
            notificationData.athleteData = null;
          }

          notifications.push(notificationData);
        }

        dispatch({
          type: lastDoc
            ? "LOAD_MORE_READ_NOTIFICATIONS"
            : "GET_READ_NOTIFICATIONS",
          payload: {
            data: notifications,
            hasMoreDocument: snapshot.docs.length >= 10,
            lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
          },
        });

        onSuccess();
      });
    } catch (error) {
      toast.error("Failed to listen to notifications");
    }
  };

let unreadUnsubscribe = null;
let athleteListeners = {};
let notificationUnsubs = [];

export const getUnreadNotifications =
  (fanId, lastVisible = null, onSuccess = () => {}) =>
  async (dispatch) => {
    try {
      const favRef = firebase
        .firestore()
        .collection("favourites")
        .where("fanId", "==", fanId);

      if (!lastVisible) {
        if (unreadUnsubscribe) unreadUnsubscribe();
        notificationUnsubs.forEach((unsub) => unsub && unsub());
        notificationUnsubs = [];
        Object.values(athleteListeners).forEach((unsub) => unsub && unsub());
        athleteListeners = {};
      }

      unreadUnsubscribe = favRef.onSnapshot(async (favSnapshot) => {
        const athleteIds = [];
        favSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.athleteId) {
            athleteIds.push(data.athleteId);
          }
        });

        if (athleteIds.length === 0) {
          dispatch({ 
            type: "GET_UNREAD_NOTIFICATIONS", 
            payload: {
              data: [],
              lastVisible: null,
              hasMoreDocument: false,
            }
          });
          onSuccess();
          return;
        }

        const athleteDataMap = {};
        athleteIds.forEach((athleteId) => {
          const userRef = firebase
            .firestore()
            .collection("users")
            .doc(athleteId);
          const unsubUser = userRef.onSnapshot((userSnap) => {
            if (userSnap.exists) {
              athleteDataMap[athleteId] = {
                id: userSnap.id,
                ...userSnap.data(),
              };
            } else {
              athleteDataMap[athleteId] = null;
            }
          });
          athleteListeners[athleteId] = unsubUser;
        });

        const chunkedIds = [];
        const idsCopy = [...athleteIds];
        while (idsCopy.length) {
          chunkedIds.push(idsCopy.splice(0, 10));
        }

        notificationUnsubs.forEach((unsub) => unsub && unsub());
        notificationUnsubs = [];

        const combinedUnsubs = [];
        const notificationsByQuery = {};
        let expectedQueries = chunkedIds.length + 1; // athlete queries + direct fan query
        let completedQueries = 0;

        // 🔹 Dispatch helper - only dispatch once all queries complete
        function pushToRedux() {
          completedQueries++;
          
          // Only dispatch when all queries have completed at least once
          if (completedQueries < expectedQueries) {
            return;
          }

          // Merge all notifications and remove duplicates by id
          const allNotifications = [];
          const seenIds = new Set();
          
          Object.values(notificationsByQuery).forEach((notifications) => {
            notifications.forEach((notif) => {
              if (!seenIds.has(notif.id)) {
                seenIds.add(notif.id);
                allNotifications.push(notif);
              }
            });
          });

          const payload = {
            data: allNotifications.sort(
              (a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis()
            ),
            lastVisible: allNotifications.length
              ? allNotifications[allNotifications.length - 1]
              : null,
            hasMoreDocument: allNotifications.length >= 10,
          };

          dispatch({
            type: lastVisible
              ? "LOAD_MORE_UNREAD_NOTIFICATIONS"
              : "GET_UNREAD_NOTIFICATIONS",
            payload,
          });

          onSuccess();
        }

        // 🔹 Query 1: Athlete-based notifications
        chunkedIds.forEach((chunk, index) => {
          let notifQuery = firebase
            .firestore()
            .collection("fan_notifications")
            .where("athleteId", "in", chunk)
            .orderBy("createdAt", "desc")
            .limit(10);

          if (lastVisible) {
            notifQuery = notifQuery.startAfter(lastVisible);
          }

          const notifUnsub = notifQuery.onSnapshot((notifSnap) => {
            const athleteNotifications = [];

            notifSnap.forEach((doc) => {
              const data = doc.data();
              const isRead = data.isRead || [];
              
              // Only include notifications that:
              // 1. Are not marked as read by this fan
              // 2. Either have no fanId (broadcast to all) OR match this specific fanId
              const isForThisFan = !data.fanId || data.fanId === fanId;
              
              if (!isRead.includes(fanId) && isForThisFan) {
                athleteNotifications.push({
                  id: doc.id,
                  ...data,
                  athleteData: athleteDataMap[data.athleteId] || null,
                });
              }
            });

            notificationsByQuery[`athlete_${index}`] = athleteNotifications;
            pushToRedux();
          });

          combinedUnsubs.push(notifUnsub);
        });

        // 🔹 Query 2: Direct fan notifications
        const directUnsub = firebase
          .firestore()
          .collection("fan_notifications")
          .where("fanId", "==", fanId)
          .orderBy("createdAt", "desc")
          .limit(10)
          .onSnapshot((directSnap) => {
            const fanNotifications = [];

            directSnap.forEach((doc) => {
              const data = doc.data();
              const isRead = data.isRead || [];
              if (!isRead.includes(fanId)) {
                fanNotifications.push({
                  id: doc.id,
                  ...data,
                });
              }
            });

            notificationsByQuery['direct_fan'] = fanNotifications;
            pushToRedux();
          });

        combinedUnsubs.push(directUnsub);
        notificationUnsubs = combinedUnsubs;
      });
    } catch (error) {
      toast.error("Failed to load unread notifications.");
    }
  };

export const markNotificationAsRead = (docId, fanId, onSuccess = () => {}) => {
  return async (dispatch) => {
    try {
      const data = {
        isRead: firebase.firestore.FieldValue.arrayUnion(fanId),
      };

      await firebase
        .firestore()
        .collection("fan_notifications")
        .doc(docId)
        .update(data);
      onSuccess();
    } catch (error) {
    }
  };
};

export const updateUserDeviceToken = (token, userId) => {
  return async (dispatch) => {
    try {
      await firebase.firestore().collection("users").doc(userId).update({
        fcmToken: token,
      });

      const updatedDoc = await firebase
        .firestore()
        .collection("users")
        .doc(userId)
        .get();

      const updatedUser = { id: updatedDoc.id, ...updatedDoc.data() };

      dispatch({
        type: "UPDATE_USER_DEVICE_TOKEN",
        payload: updatedUser,
      });
    } catch (error) {
    }
  };
};
